/**
 * useArrivalReveal ✦ — iter197
 * ─────────────────────────────────────────────────────────────
 * Gate cinematic arrival animations (staggered section reveals,
 * hero fades, drifting words) so they ONLY play the first time
 * a guest arrives in this session.
 *
 * The contract from Craig:
 *
 *   "Back should feel instantaneous and emotionally continuous —
 *    no white repaint, no skeleton, no 'Loading…', no hero
 *    re-entry, no re-triggered stagger sequence."
 *
 * Once a guest has watched the room compose itself, they should
 * never see it re-compose for the rest of the night. Returning
 * via back navigation must feel like the room was always there.
 *
 * Returns:
 *   shouldReveal — true the first time the page mounts in this
 *                  session; false on every subsequent remount
 *                  (which is what a browser-back produces).
 *
 * Implementation:
 *   • sessionStorage flag set on FIRST mount.
 *   • Subsequent mounts read the flag synchronously — no race.
 *   • Tab-scoped → opening the link in a fresh tab still gets the
 *     full cinematic arrival.
 *
 * Usage in a component:
 *
 *   const shouldReveal = useArrivalReveal(`booth_launcher_${event_id}`);
 *   ...
 *   style={{
 *     opacity: shouldReveal ? 0 : 1,
 *     animation: shouldReveal
 *       ? 'boothSectionReveal 900ms ease-out 0.9s forwards'
 *       : 'none',
 *   }}
 */
import { useRef } from 'react';

const PREFIX = 'eventdrop_arrived_';

const hasArrived = (key) => {
  if (!key) return false;
  try { return sessionStorage.getItem(`${PREFIX}${key}`) === '1'; }
  catch { return false; }
};

const markArrived = (key) => {
  if (!key) return;
  try { sessionStorage.setItem(`${PREFIX}${key}`, '1'); }
  catch { /* no-op */ }
};

export function useArrivalReveal(key) {
  // Synchronous read on the very first render — the result locks
  // for the lifetime of this mount via useRef. Marking happens on
  // the same render so back navigation within the session reads
  // truthy.
  const result = useRef(null);
  if (result.current === null) {
    const already = hasArrived(key);
    result.current = !already;
    if (!already) markArrived(key);
  }
  return result.current;
}

export default useArrivalReveal;
